import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface Organization {
  id: string;
  name: string;
  slug: string;
  subdomain: string;
  logo_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
}

/**
 * Hook to detect organization from subdomain
 * Works with format: {subdomain}.yourdomain.com
 * Falls back to path-based routing for development/preview
 */
export function useSubdomainOrg() {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const detectOrganization = async () => {
      try {
        const hostname = window.location.hostname;
        
        // Check if we're on a subdomain (not localhost, not preview URL, not main domain)
        const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1";
        const isPreview = hostname.includes("lovableproject.com") || hostname.includes("lovable.app");
        
        // Extract subdomain from hostname
        // e.g., sibus.myapp.com -> sibus
        // e.g., sibus.training.myapp.com -> sibus
        const parts = hostname.split(".");
        
        let subdomain: string | null = null;
        
        if (!isLocalhost && !isPreview && parts.length >= 3) {
          // Production with custom domain: subdomain.domain.tld
          subdomain = parts[0];
        }
        
        if (subdomain) {
          // Fetch organization by subdomain via SECURITY DEFINER RPC (anonymous-safe)
          const { data, error: fetchError } = await supabase
            .rpc("lookup_org_by_subdomain", { p_subdomain: subdomain });
          
          if (fetchError) throw fetchError;
          
          const org = data?.[0];
          if (org) {
            setOrganization(org as Organization);
          } else {
            setError("Organization not found");
          }
        } else {
          // No subdomain detected - use path-based routing (development mode)
          setOrganization(null);
        }
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    detectOrganization();
  }, []);

  // isSubdomainMode should be true whenever a subdomain was detected,
  // even if the org was not found (so the "not found" screen shows)
  const isSubdomainMode = !!organization || !!error;
  return { organization, loading, error, isSubdomainMode };
}

/**
 * Get the base URL for a specific organization
 * Used for generating links in emails, sharing, etc.
 */
export function getOrgUrl(subdomain: string, baseDomain?: string): string {
  const domain = baseDomain || import.meta.env.VITE_APP_DOMAIN || window.location.host;
  
  // For preview/localhost, use path-based URL
  if (domain.includes("localhost") || domain.includes("lovable")) {
    return `${window.location.origin}/${subdomain}`;
  }
  
  // For production, use subdomain URL
  return `https://${subdomain}.${domain}`;
}
